/**
 * Parse API due dates as local calendar days (avoid UTC midnight shift on YYYY-MM-DD).
 */

export function parseLocalDate(iso) {
  if (!iso) return null;
  const s = String(iso).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    d.setHours(0, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

export function startOfToday() {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}

/** Days from today to due (negative = overdue / past due). */
export function daysUntilDue(iso, today = startOfToday()) {
  const d = parseLocalDate(iso);
  if (!d) return null;
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

/**
 * Mutually exclusive buckets (same timeline, one place only):
 *   overdue  = past due (days < 0)
 *   urgent / Due soon = still due, within 0–30 days (~1 month)
 *   upcoming = still due, 31–60 days (look-ahead only ?? not "Due soon")
 * Overdue and Urgent never overlap ?? you can have overdue with urgent = 0.
 */
export function dueBucketFromDays(days) {
  if (days == null) return null;
  if (days < 0) return 'overdue';
  if (days <= 30) return 'urgent';
  if (days <= 60) return 'upcoming';
  return null;
}

/** True only while still due and within 30 days ?? never for overdue. */
export function isUrgentWindow(days) {
  return days != null && days >= 0 && days <= 30;
}

/** True when due date has passed. */
export function isOverdue(days) {
  return days != null && days < 0;
}

/** Statuses that leave the Due / Over Due work queue. */
export const DUE_QUEUE_CLOSED_STATUSES = ['COMPLETED', 'REJECTED'];

/**
 * Show "Over Due" only for open work that is past due.
 * Completed/Rejected must never show Over Due (date alone is not enough).
 */
export function isOpenPastDue(status, dueDateIso, today = startOfToday()) {
  if (status && DUE_QUEUE_CLOSED_STATUSES.includes(String(status).toUpperCase())) {
    return false;
  }
  const days = daysUntilDue(dueDateIso, today);
  return isOverdue(days);
}

/** Inspection due dates are always dd/mm/yyyy, independent of the browser locale. */
export function formatDueDateLocal(iso) {
  const d = parseLocalDate(iso);
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}
